import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from '@mui/material'
import { Box } from '@mui/system'
import { useTranslation } from 'react-i18next'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import UploadButton from '@/components/ui/buttons/UploadButton'
import { Document } from '@/core/models/document.model'
import DocumentComponent from '@/components/group/Document'
import { useEffect, useState } from 'react'
import _ from 'lodash'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import CircularProgress from '@mui/material/CircularProgress'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

export const AddBackgroundDialog = ({
  open,
  onClose,
  onAddBackground,
  isUploading,
  backgrounds,
  onDelete,
  isDeleting,
}: {
  open: boolean
  onClose: () => void
  onAddBackground: (file: File) => void
  isUploading: boolean
  backgrounds?: Document[]
  onDelete?: (documents: Document[]) => void
  isDeleting?: boolean
}) => {

  const { t } = useTranslation()
  const [selectedDocuments, setSelectedDocuments] = useState<Document[]>([])
  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (!!selectedDocuments.length && !_.intersectionBy(backgrounds, selectedDocuments, 'key').length) {
      setSelectedDocuments([])
    }
  }, [backgrounds, selectedDocuments])

  const handleChangeDocumentSelection = (document: Document) => (event: React.ChangeEvent, checked: boolean) => {
    setSelectedDocuments((prevSelectedDocuments = []) => {
      return checked
        ? [...prevSelectedDocuments, document]
        : _.filter(prevSelectedDocuments, (prevSelectedDocument) => {
          return !_.isEqual(prevSelectedDocument, document)
        })
    })
  }

  const toggleDeleteConfirmationDialog = () => {
    setDeleteConfirmationDialogOpen((prevState) => !prevState)
  }

  const handleDelete = () => {
    onDelete?.(selectedDocuments)
    toggleDeleteConfirmationDialog()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <Stack spacing={3} alignItems="center">
          <Typography variant="h6" align="center">
            {t('page.admin.background.title')}
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <UploadButton
              accept='image/*'
              onUpload={(file) => {
                onAddBackground(file)
              }}
              loading={isUploading}
            />
          </Stack>

          <Box width="100%">
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">
                  {t('page.admin.background.gallery')}
                </Typography>
                {selectedDocuments.length > 0 && (
                  <Button
                    disabled={isDeleting}
                    onClick={toggleDeleteConfirmationDialog}
                    startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteRoundedIcon />}
                    color="error"
                    variant="contained"
                    size="small"
                  >
                    {t('common:button.delete.label', { count: selectedDocuments.length })}
                  </Button>
                )}
              </Stack>

              <Grid container spacing={2} justifyContent="center">
                {backgrounds?.map((document, index) => (
                  <Grid key={index} item xs={12} sm={6} md={4}>
                    <Box sx={{ position: 'relative' }}>
                      <DocumentComponent
                        disableAnnotation={true}
                        disableChangeName={true}
                        extension={document.filename.split('.')[1]}
                        fileKey={document.key}
                        name={document.filename}
                        src={document.url}
                      />
                      <Checkbox
                        checked={_.findIndex(selectedDocuments, (selectedDocument) => _.isEqual(selectedDocument, document)) !== -1}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: '4px'
                        }}
                        onChange={handleChangeDocumentSelection(document)}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button onClick={onClose} variant="outlined">
          {t('common:button.cancel.label')}
        </Button>
      </DialogActions>

      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        onClose={toggleDeleteConfirmationDialog}
        onDelete={handleDelete}
        open={deleteConfirmationDialogOpen}
        title={t('dialog.deleteConfirmation.documents.title', { count: selectedDocuments.length })}
        message={t('dialog.deleteConfirmation.documents.message')}
      />
    </Dialog>
  )
}